/**
 * FRONT-2594 — pure decisions behind the Canvas document editor.
 */
import type { CanvasDraft } from './canvas-storage';
import { ApiError } from '~/lib/api-client';
import { BASE_PENDING, isBlankDraft } from './save-decision';

/** A draft is dirty only when it differs from what the server holds. */
export function isDraftDirty(draft: CanvasDraft | null, serverContent: string): boolean {
  return draft !== null && draft.content !== serverContent;
}

/**
 * Show the "Eddy changed this document" notice: a dirty draft whose base is no
 * longer the server's version (seen proactively from the detail query), or a
 * save that the fresh pre-save check refused (`saveConflict`).
 */
export function showConflictNotice(input: {
  draft: CanvasDraft | null;
  serverContent: string;
  serverUpdatedAt: string | null;
  saveConflict: boolean;
}): boolean {
  if (!isDraftDirty(input.draft, input.serverContent)) return false;
  // A pending base has no timestamp to disagree with; only a refused save counts.
  if (input.draft!.baseUpdatedAt === BASE_PENDING) return input.saveConflict;
  return input.saveConflict || input.draft!.baseUpdatedAt !== input.serverUpdatedAt;
}

export const EMPTY_DRAFT_REASON = "A document can't be empty.";
export const CONFLICT_SAVE_REASON = "Choose “Keep mine” or “Load EDDY's version” first.";

/**
 * The ONE decision behind the Save button: it is disabled iff this returns a
 * sentence, and that sentence is shown as visible text next to it.
 */
export function saveBlockReason(input: { content: string; conflict: boolean }): string | null {
  if (isBlankDraft(input.content)) return EMPTY_DRAFT_REASON;
  if (input.conflict) return CONFLICT_SAVE_REASON;
  return null;
}

/**
 * What the editor holds once a save finishes. The save sent `sent`; the person
 * may have typed more while it was in flight (`latestText` is what the box shows
 * NOW). Only a draft that still equals what was sent is cleared. Anything else is
 * kept, with its base moved to the version the save produced, so those keystrokes
 * are neither lost nor mistaken for a conflict with the save itself.
 */
export function draftAfterSave(input: {
  sent: string;
  latestText: string;
  savedUpdatedAt: string | null;
}): CanvasDraft | null {
  if (input.latestText === input.sent) return null;
  return { content: input.latestText, baseUpdatedAt: input.savedUpdatedAt ?? BASE_PENDING };
}

/**
 * The base a draft started NOW takes. A running draft keeps its own. A new one
 * takes the cached row's updatedAt, unless the post-save re-read failed and the
 * refetch has not landed (`basePending`): the cache is then the row from BEFORE
 * the save, so the draft is marked pending instead of inheriting it.
 */
export function baseForEdit(input: {
  draft: CanvasDraft | null;
  serverUpdatedAt: string;
  basePending: boolean;
}): string {
  if (input.draft && input.draft.baseUpdatedAt !== null) return input.draft.baseUpdatedAt;
  return input.basePending ? BASE_PENDING : input.serverUpdatedAt;
}

/**
 * Once the refetched row has landed (`basePending` is over), a draft still
 * marked pending takes its base from that row. Null when there is nothing to do.
 */
export function adoptPendingBase(input: {
  draft: CanvasDraft | null;
  serverUpdatedAt: string;
  basePending: boolean;
}): CanvasDraft | null {
  if (input.basePending || !input.draft || input.draft.baseUpdatedAt !== BASE_PENDING) return null;
  return { ...input.draft, baseUpdatedAt: input.serverUpdatedAt };
}

export type DocumentBranch =
  /** First load still running and nothing cached. */
  | 'loading'
  /** Nothing cached and the load failed (not a 404, or a 404 with no draft to protect). */
  | 'error'
  /** Gone (404) while the editor is on screen: keep the editor mounted, read-only, so the live text can be copied. */
  | 'gone-editor'
  /** Gone (404) and a stored draft differs from anything cached: show it read-only with Copy. */
  | 'gone-draft'
  /** Gone (404) and nothing worth keeping. */
  | 'gone'
  /** Cached data is shown; a refetch failed (non-404), so a small note rides above it. */
  | 'content-stale'
  | 'content';

/**
 * Which branch the document view renders. A failed BACKGROUND refetch must not
 * replace a cached document (and the editor with its draft) with an error page:
 * the error branch is only for "nothing to show".
 */
export function documentBranch(input: {
  isPending: boolean;
  isError: boolean;
  status: number | null;
  hasData: boolean;
  view: string;
  /** A stored draft exists that differs from the cached text (or there is no cached text). */
  hasStoredDraft: boolean;
}): DocumentBranch {
  if (input.isPending) return 'loading';
  if (input.isError && input.status === 404) {
    if (input.hasData && input.view === 'edit') return 'gone-editor';
    return input.hasStoredDraft ? 'gone-draft' : 'gone';
  }
  if (input.isError) return input.hasData ? 'content-stale' : 'error';
  return 'content';
}

/**
 * The version a document PUT is checked against (`baseUpdatedAt`, FRONT-2600:
 * the server answers 409 when the row has moved past it). A normal save sends the
 * draft's own base; a draft whose base is still pending has none, so it takes the
 * row the pre-save read just returned. "Keep mine" is a deliberate overwrite: it
 * sends the NEWEST row's version (the one the conflict notice is about), never the
 * draft's old base, or the same save would be refused again.
 */
export function baseForWrite(input: {
  force: boolean;
  draftBase: string | null;
  /** The row's updatedAt as the pre-save read (or, for a forced save, the cache) holds it. */
  currentUpdatedAt: string;
}): string {
  if (input.force) return input.currentUpdatedAt;
  if (input.draftBase === null || input.draftBase === BASE_PENDING) return input.currentUpdatedAt;
  return input.draftBase;
}

/** The server refused a save because the document changed since its base (HTTP 409). */
export function isStaleSaveRefusal(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}
